import React, { useState } from "react"
import { FirstForm, CombinedForm, BusinessForm } from "./Allforms"
import useAuth from "../../context/userContext"
import Verification from "./EmailConfirmation"
import { ErrorNotification } from "../../utils"
import { isPasswordValid, PASSWORD_VALIDATION_MESSAGE } from "../../utils/Formatter"
import * as yup from "yup"
import { useFormik, Form } from "formik"
import { useLocationCascade } from "../../hooks/useLocations"

const Signup = () => {
  const allsteps = ["First Step", "Second Step", "Third Step"]
  const { signup } = useAuth()

  const [activeStep, setActiveStep] = React.useState(0)
  const [steps, setSteps] = React.useState(allsteps)
  const [loading, setLoading] = useState(false)
  const [formType, setFormType] = useState("personal") // personal, business

  const formik = useFormik({
    initialValues: {
      business_name: "",
      firstname: "",
      lastname: "",
      password: "",
      confirmpassword: "",
      phone: "",
      ghana_card_number: "",
      email: "",
      region: "",
      district: "",
      community: "",
      dob: "",
      gender: "",
      user_type: "",
      country: "",
      factory_size: "",
      materials_source: [],
      is_business_account: false,
      filepath: null
    },
    validationSchema: yup.object({
      business_name: formType === "personal"
        ? yup.string().optional()
        : yup.string()
          .required("Business name is required")
          .matches(/^[a-zA-Z0-9\s&.,'-]+$/, "Business name can only contain letters, numbers, spaces, and common punctuation"),
      firstname: formType === "personal" ? yup.string().required("First name is required").matches(/^[a-zA-Z]+$/, "First name must contain only letters") : yup.string().optional(),
      user_type: yup.string().required("User type is required"),
      lastname: formType === "personal" ? yup.string().required("Last name is required").matches(/^[a-zA-Z]+$/, "Last name must contain only letters") : yup.string().optional(),
      password: yup
        .string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters")
        .test("password-strength", PASSWORD_VALIDATION_MESSAGE, (value) => isPasswordValid(value)),
      confirmpassword: yup
        .string()
        .oneOf([yup.ref("password"), null], "Passwords must match")
        .required("Confirm Password is required"),
      // allow only numbers
      phone: yup
        .string()
        .required("Phone number is required")
        .matches(/^\d+$/, "Phone number must contain only numbers")
        .min(10, "Phone number must be at least 10 characters")
        .max(15, "Phone number must not be more than 15 characters"),
      ghana_card_number: yup
        .string()
        .optional(),
      ghana_card_image: yup.string().optional(),
      id_type: yup.string().optional(),
      doc_url: yup.mixed().optional(),
      email: yup
        .string()
        .required("Email is required")
        .email("Invalid email address"),
      region: yup.string().required("Region is required"),
      dob: formType === "personal" ? yup
        .date()
        .required("Date of birth is required")
        .max(new Date(), "Date of birth cannot be in the future")
        .test("age", "You must be at least 18 years old", function (value) {
          if (!value) return false
          const today = new Date()
          const birthDate = new Date(value)
          let age = today.getFullYear() - birthDate.getFullYear()
          const monthDiff = today.getMonth() - birthDate.getMonth()
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--
          }
          return age >= 18
        }) : yup.date().optional(),
      gender: formType === "business" ? yup.string().optional() : yup.string().required("Gender is required"),
      country: yup.string().required("Country is required"),
      district: yup.string().required("District is required"),
      community: yup.string().required("Community is required"),
      factory_size: yup.string().when('user_type', {
        is: 'processor',
        then: yup.string().required("Factory size is required"),
        otherwise: yup.string().optional()
      }),
      materials_source: yup.array().when('user_type', {
        is: 'processor',
        then: yup.array().min(1, "Please select at least one source of raw materials").required("Source of raw materials is required"),
        otherwise: yup.array().optional()
      }),
    }),
    onSubmit: async (values) => {

      if (values) {
        await SIGNUP({
          ...values,
          is_business_account: formType === "business" ? true : false,
          business_name: values.business_name
        })
      } else {
        ErrorNotification("Please fill all fields")
      }
    },
  })

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const resetForm = () => {
    formik.resetForm()
  }

  const handleNext = () => {
    if (activeStep === 0) {
      formik.validateForm().then(errors => {
        const requiredFields = ['user_type', 'password', 'confirmpassword']
        if (formType === "personal") {
          requiredFields.push('firstname', 'lastname', 'dob')
        } else if (formType === "business") {
          requiredFields.push('business_name')
        }
        const hasErrors = requiredFields.some(field => errors[field])

        if (formik.values.user_type === "processor") {
          const processorFields = ['factory_size', 'materials_source']
          const hasProcessorErrors = processorFields.some(field => errors[field])
          if (hasErrors || hasProcessorErrors) {
            ErrorNotification("Please fill all required fields correctly")
            return
          }
        } else if (hasErrors) {
          ErrorNotification("Please fill all required fields correctly")
          return
        }
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
      })
    } else if (activeStep === 1) {
      formik.validateForm().then(errors => {
        const requiredFields = ['email', 'region', 'district', 'community', 'phone', 'country']
        const hasErrors = requiredFields.some(field => errors[field])
        if (hasErrors) {
          ErrorNotification("Please fill all required fields correctly")
        } else {
          setActiveStep((prevActiveStep) => prevActiveStep + 1)
        }
      })
    } else if (activeStep === 2) {
      formik.handleSubmit()
    }
  }

  function handleChange(e) {
    const { name, value, files } = e.target

    // Handle file inputs
    if (files && files.length > 0) {
      const file = files[0]
      // Store the File object for form submission
      formik.setFieldValue(name, file)

      // Also store filepath for business documents (filepath = doc_url)
      if (name === 'doc_url' && formType === 'business') {
        formik.setFieldValue('filepath', file)
      }
    } else {
      // Handle regular inputs - use formik's handleChange for proper validation
      formik.handleChange(e)
    }
  }

  function setGender(value) {
    formik.setFieldValue("gender", value)
  }

  function resetLocation() {
    formik.setFieldValue("region", "")
    formik.setFieldValue("district", "")
    formik.setFieldValue("community", "")
  }

  const SIGNUP = async (values) => {
    setLoading(true)
    try {
      // Ensure files are included in the submission
      const submissionValues = {
        ...values,
        ghana_card_image: values.ghana_card_image instanceof File ? values.ghana_card_image : values.ghana_card_image,
        doc_url: values.doc_url instanceof File ? values.doc_url : values.doc_url,
        filepath: values.filepath instanceof File ? values.filepath : (values.filepath || (values.doc_url && formType === 'business' ? values.doc_url : null))
      }
      await signup(submissionValues)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      ErrorNotification(error)
    }
  }

  const {
    regionNames,
    districts,
    communities,
    isLoading: locationsLoading,
  } = useLocationCascade(formik.values.region, formik.values.district)

  return (
    <div className="bg-white  dark:bg-dark">
      {activeStep === 0 ? (
        <FirstForm
          formType={formType}
          resetForm={resetForm}
          setFormType={setFormType}
          handleNext={handleNext}
          onSubmit={formik.handleSubmit}
          values={formik.values}
          errors={formik.errors}
          touched={formik.touched}
          handleBlur={formik.handleBlur}
          handleChange={handleChange}
          setGender={setGender}
          activeStep={activeStep}
        />
      ) : activeStep === 1 ? (
          <CombinedForm
            formType={formType}
          handleBack={handleBack}
            setFormType={setFormType}
            formik={formik}
            handleNext={handleNext}
          values={formik.values}
          errors={formik.errors}
          touched={formik.touched}
          handleBlur={formik.handleBlur}
            handleChange={handleChange}
            regionNames={regionNames}
            district={districts}
            community={communities}
            districtLoading={locationsLoading}
            communityLoading={locationsLoading}
            activeStep={activeStep}
          />
        ) : activeStep === 2 ? (
          <BusinessForm
              formType={formType}
              setFormType={setFormType}
              handleBack={handleBack}
              handleNext={handleNext}
              values={formik.values}
              errors={formik.errors}
              touched={formik.touched}
              handleBlur={formik.handleBlur}
              handleChange={handleChange}
              handleSubmit={formik.handleSubmit}
              loading={loading}
              activeStep={activeStep}
        />
      ) : (
        <Verification />
      )}
    </div>
  )
}

export default Signup
